import { LegalPage, LegalSection } from './LegalPage';

export default function PrivacyPage() {
  return (
    <LegalPage title="Privacy Policy" updated="October 10, 2026">
      <LegalSection heading="Who we are">
        <p>
          MasterStax ("the site") is a student resources hub for university math courses and a product of
          MikaLabs (<a href="https://mikalabs.org" rel="noopener noreferrer" target="_blank">mikalabs.org</a>);
          MikaLabs operates it ("we", "us"). This policy explains what information we collect and
          how it is used. The site is designed so that <strong>you can browse all course content without
          signing in</strong>.
        </p>
      </LegalSection>

      <LegalSection heading="Information we collect">
        <p>We only collect what the features actually need:</p>
        <ul style={{ paddingLeft: 20 }}>
          <li>
            <strong>Account (optional).</strong> If you sign in with Google, we receive your name, email
            address, and profile photo via Google Sign-In. These are stored in our database only to
            authenticate you, save your progress across devices, and (for administrator accounts) grant
            access to the admin dashboard.
          </li>
          <li>
            <strong>Study progress.</strong> Which resources you marked as completed. If you do not sign in,
            this stays only in your browser's local storage (on your device).
          </li>
          <li>
            <strong>Uploaded files.</strong> Only administrators can upload files (PDFs, images); these are
            stored in Google Cloud Storage and are public by design.
          </li>
          <li>
            <strong>Technical data.</strong> Our hosting and backend providers (Netlify, Firebase/Google) log
            standard request data such as IP address, browser type, and page requests, as described in their
            own privacy terms, to operate and secure the service.
          </li>
        </ul>
      </LegalSection>

      <LegalSection heading="Cookies and advertising (coming soon)">
        <p>
          MasterStax is currently ad-free. We plan to introduce Google AdSense advertising to keep the hub
          free for students. When ads are enabled:
        </p>
        <ul style={{ paddingLeft: 20 }}>
          <li>Third-party vendors, including Google, will use cookies to serve ads based on prior visits.</li>
          <li>
            Google's use of advertising cookies enables it and its partners to serve ads based on your visit
            to this and other sites. You may opt out of personalized advertising by visiting{' '}
            <a href="https://www.google.com/settings/ads" rel="noopener noreferrer" target="_blank">
              Google Ads Settings
            </a>{' '}
            or <a href="https://www.aboutads.info/choices" rel="noopener noreferrer" target="_blank">
              www.aboutads.info
            </a>.
          </li>
          <li>Ads will never appear inside the admin dashboard, and we will not share your personal data with advertisers beyond what Google's ad systems standardly handle.</li>
        </ul>
      </LegalSection>

      <LegalSection heading="How we use information">
        <ul style={{ paddingLeft: 20 }}>
          <li>To authenticate you and sync your study progress across devices.</li>
          <li>To operate, maintain, and secure the service (including abuse prevention via Firestore security rules).</li>
          <li>To display content relevant to the course you selected.</li>
          <li>Once enabled, to serve advertising that funds the site.</li>
        </ul>
        <p>We do not sell or rent your personal information.</p>
      </LegalSection>

      <LegalSection heading="Third-party services">
        <p>
          The site uses Google Firebase (Authentication, Cloud Firestore, Cloud Storage) for its backend and
          Netlify for hosting. Each operates under its own privacy policy:
        </p>
        <ul style={{ paddingLeft: 20 }}>
          <li>
            <a href="https://firebase.google.com/support/privacy" rel="noopener noreferrer" target="_blank">
              Firebase Privacy Policy
            </a>
          </li>
          <li>
            <a href="https://www.netlify.com/privacy/" rel="noopener noreferrer" target="_blank">
              Netlify Privacy Policy
            </a>
          </li>
          <li>
            <a href="https://policies.google.com/privacy" rel="noopener noreferrer" target="_blank">
              Google Privacy Policy
            </a>
          </li>
        </ul>
        <p>
          The site links to third-party learning resources (OpenStax, Khan Academy, YouTube, and others).
          We are not responsible for their privacy practices — please review their policies.
        </p>
      </LegalSection>

      <LegalSection heading="Data retention and deletion">
        <p>
          Published course content is public by design and may be cached by search engines and CDNs. Your
          personal account data (name, email, progress) is kept until deletion is requested. To delete your
          account and associated data, contact the administrator via the details below; deletion is also
          possible by deleting your Google account (removes your profile) and by clearing this site's site
          data in your browser (removes local progress).
        </p>
      </LegalSection>

      <LegalSection heading="Children">
        <p>
          The site is a university course tool intended for adult and older-teen students. We do not knowingly
          collect personal information from children under 13. If you believe a child's data has been
          collected, please contact us and we will delete it.
        </p>
      </LegalSection>

      <LegalSection heading="Your rights">
        <p>
          Depending on your region (for example the EU/EEA under GDPR or California under the CCPA), you may
          have rights to access, correct, export, or delete your personal information, and to object to or
          restrict its processing. We will respond to genuine requests promptly.
        </p>
      </LegalSection>

      <LegalSection heading="Changes to this policy">
        <p>
          If we materially change this policy we will update the date above and, where the law requires it,
          give more prominent notice. Your continued use of the site after changes means you accept the
          updated policy.
        </p>
      </LegalSection>

      <LegalSection heading="Contact">
        <p>
          For privacy questions or data requests, email MikaLabs at{' '}
          <a href="mailto:support@mikalabs.org">support@mikalabs.org</a> or reach us through{' '}
          <a href="https://mikalabs.org" rel="noopener noreferrer" target="_blank">mikalabs.org</a>.
        </p>
      </LegalSection>
    </LegalPage>
  );
}
